import moment from 'moment';
import { useCallback, useMemo } from 'react';

import Cross from '../../../../assets/images/icon-filled-cross-dark.svg';
import CheckTickIcon from '../../../../assets/images/icon-filled-success-tick-white.svg';
import { CheckCircleIcon } from '../../../../icons/CheckCircleIcon';
import { ClockIcon } from '../../../../icons/ClockIcon';
import useSubscriptionContext, { CONFIRMATION_TYPE } from '../useSubscriptionContext';

export const Confirmation = ({
  onClose,
  confirmationType,
}: {
  onClose: () => void;
  confirmationType: CONFIRMATION_TYPE;
}) => {
  const {
    clueyPlus: { showConfirmationDialog },
  } = useSubscriptionContext();

  const clueyPlusDiscountApplied = useCallback(() => {
    return (
      <>
        <div className="flex flex-col items-center gap-y-3">
          <img src={CheckTickIcon} alt="check tick icon" className="h-10 w-10" />
          <h2 className="merriweather text-center text-2xl font-bold leading-tight">Your discount is being applied</h2>
        </div>
        <p className="px-2 text-center text-sm">
          Your Cluey+ subscription will be $20/month (33% off) for the next 3 months.
        </p>
      </>
    );
  }, []);

  const clueyPlusSubscriptionCreated = useCallback(() => {
    return (
      <>
        <div className="flex flex-col items-center gap-y-4">
          <ClockIcon className="!fill-blood-orange-4" width={40} height={40} />
          <h2 className="merriweather text-center text-2xl font-bold leading-tight">
            Your Cluey+ subscription is being created
          </h2>
          <p className="mt-3 w-full text-center">Details will appear here when ready</p>
        </div>
      </>
    );
  }, []);

  const clueyPlusSubscriptionCancelled = useCallback(() => {
    return (
      <>
        <div className="flex flex-col items-center gap-y-3">
          <img src={CheckTickIcon} alt="check tick icon" className="h-10 w-10" />
          <h2 className="merriweather text-center text-2xl font-bold leading-tight">Cluey+ subscription cancelled</h2>
        </div>
        <p className="px-2 text-center text-sm">
          Your Cluey+ subscription has been cancelled. You will no longer be charged $30/month.
        </p>
      </>
    );
  }, []);

  const clueyPlusDowngradeSubscription = useCallback(() => {
    return (
      <>
        <div className="flex flex-col items-center gap-y-4">
          <CheckCircleIcon className="!fill-green-4" width={40} height={40} />
          <h2 className="merriweather text-center text-2xl font-bold leading-tight">Cluey+ subscription confirmed</h2>
          <p className="mt-3 w-full text-center">You've successfully switched to a Cluey+ subscription.</p>
          <p className="mt-3 w-full text-center">
            You'll still have full access to your current tutoring sessions until they wrap up.
          </p>
        </div>
        <div className="flex flex-col gap-y-6 rounded-2xl border border-blood-orange-3 bg-blood-orange-2/40 p-6">
          <div className="flex flex-col gap-y-2">
            <p className="text-sm font-medium">Your discounted Cluey+ subscription starts</p>
            <div>
              <p className="font-lg font-semibold text-blood-orange-5">{moment().format('DD MMM')} (Today)</p>
              <p className="font-semibold">$20/month for first 3 months</p>
              <p className="text-xs">Billed monthly</p>
            </div>
          </div>
          <div className="flex flex-col gap-y-2">
            <p className="text-sm font-medium">Regular ongoing payments will start from</p>
            <div>
              <p className="font-lg font-semibold text-blood-orange-5">{moment().add(3, 'months').format('DD MMM')}</p>
              <p className="font-semibold">$30/month</p>
              <p className="text-xs">Billed monthly</p>
            </div>
          </div>
        </div>
      </>
    );
  }, []);

  const confirmationMessage = useCallback(() => {
    switch (confirmationType) {
      case CONFIRMATION_TYPE.CLUEY_PLUS_DISCOUNT_APPLIED:
        return clueyPlusDiscountApplied();
      case CONFIRMATION_TYPE.CLUEY_PLUS_SUBSCRIPTION_CREATED:
        return clueyPlusSubscriptionCreated();
      case CONFIRMATION_TYPE.CLUEY_PLUS_SUBSCRIPTION_CANCELLED:
        return clueyPlusSubscriptionCancelled();
      case CONFIRMATION_TYPE.CLUEY_PLUS_DOWNGRADE_SUBSCRIPTION:
        return clueyPlusDowngradeSubscription();
      default:
        return null;
    }
  }, [
    confirmationType,
    clueyPlusDiscountApplied,
    clueyPlusSubscriptionCreated,
    clueyPlusSubscriptionCancelled,
    clueyPlusDowngradeSubscription,
  ]);

  // Will return false if the confirmation type is CLUEY_PLUS_SUBSCRIPTION_CREATED and the subscription type is TRIAL or BUNDLED
  const showCloseButton = useMemo(() => {
    return !(
      confirmationType === CONFIRMATION_TYPE.CLUEY_PLUS_SUBSCRIPTION_CREATED ||
      confirmationType === CONFIRMATION_TYPE.CLUEY_PLUS_DISCOUNT_APPLIED
    );
  }, [confirmationType]);

  return (
    <div
      className="z-9 inset-0 flex flex-col rounded-3xl border border-grey-2/20 bg-white p-7 shadow-md"
      data-testid="cancel-tutoring-modal"
    >
      {showCloseButton && (
        <div className="flex flex-row justify-end">
          <button
            type="button"
            onClick={() => {
              if (confirmationType === CONFIRMATION_TYPE.CLUEY_PLUS_DOWNGRADE_SUBSCRIPTION) {
                showConfirmationDialog(CONFIRMATION_TYPE.CLUEY_PLUS_SUBSCRIPTION_CREATED);
              } else {
                onClose();
              }
            }}
            className="p-1 hover:bg-white/10"
            aria-label="Close"
          >
            <img src={Cross} alt="close icon" />
          </button>
        </div>
      )}
      <div className="flex flex-col items-center gap-y-6">{confirmationMessage()}</div>
    </div>
  );
};
